import type { RefObject } from 'react'
import { useTranslation } from 'react-i18next'
import { useSelector } from 'react-redux'
import KibDivider from '@chewy/kib-divider-react'
import { KibField, KibSelect } from '@chewy/kib-fields-new-react'
import {
  DateFormat,
  ErrorTooltip,
  type Field,
  LanguageUtils,
  moment,
  type Nil,
  PermissionArea,
  RoleName,
} from '@pbt/pbt-ui-components'

import AddToAppointmentCard from '~/components/common/addToAppointment/AddToAppointmentCard'
import ContactTargetSlot from '~/components/common/ContactTargetSlot'
import { KibUserMultiSelect } from '~/components/common/inputs/user-multi-select/KibUserMultiSelect'
import { KibUserSelect } from '~/components/common/inputs/user-select/KibUserSelect'
import { UserSelectFilterScope } from '~/components/common/inputs/UserSelect'
import DialogNames from '~/constants/DialogNames'
import FeatureToggle from '~/constants/featureToggle'
import { useCreatableTaskTypes } from '~/store/hooks/tasks'
import {
  getCRUDByArea,
  getCurrentBusinessWellnessPlansEnabled,
} from '~/store/reducers/auth'
import { getFeatureToggle } from '~/store/reducers/constants'
import { getTasksIsFetching } from '~/store/reducers/tasks'
import { type ContactSlot, type Task, TeamFilter } from '~/types'
import useDialog from '~/utils/useDialog'
import useHasRole from '~/utils/useHasRole'
import useIsCurrentContextItem from '~/utils/useIsCurrentContextItem'

import type { BaseTemplateInputHandle } from '../../template-inputs/BaseTemplateInput/BaseTemplateInput'
import NotesTemplateInput from '../../template-inputs/NotesTemplateInput'
import TaskRecurrenceSettings, {
  type TaskRecurrenceSettingsHandle,
} from '../TaskRecurrenceSettings'
import { useInitializeClientConciergeOnCreation } from '../taskUtils'

import '@chewy/kib-divider-styles/dist/kib-divider.css'
import '@chewy/kib-fields-styles/dist/styles/kib-input-group.css'
import '@chewy/kib-choices-styles/dist/styles/kib-radio.css'
import '@chewy/kib-fields-new-styles/dist/styles/kib-field.css'
import '@chewy/kib-fields-new-styles/dist/styles/kib-select.css'
import '@chewy/kib-fields-new-styles/dist/kib-fields-new.css'
import '@chewy/kib-interstitials-styles/dist/styles/kib-spinner.css'
import styles from './TaskFormV2.module.scss'

interface TaskFormV2Props {
  assigneeClientAndPatientIsValid: boolean
  dueDate: string | Nil
  fields: {
    assigneeId: Field
    clientNotes: Field
    copyToIds: Field
    forClient: Field
    instructions: Field
    name: Field
    notes: Field
    touched: boolean
    typeId: Field
  }
  instructionsRef: RefObject<BaseTemplateInputHandle>
  notesRef: RefObject<BaseTemplateInputHandle>
  rootRef: RefObject<HTMLDivElement>
  selectedAppointmentId: string | null
  selectedClientId: string | Nil
  selectedPatientId: string | Nil
  setSelectedAppointmentId: (id: string | null) => void
  setSlotInfo: (info: ContactSlot) => void
  shouldUseVCRBehavior: boolean | undefined
  slotInfo: ContactSlot
  soapId: string | undefined
  task: Task | undefined
  taskRecurrenceSettingsRef: RefObject<TaskRecurrenceSettingsHandle>
}

export const TaskFormV2 = ({
  dueDate: dueDateProp,
  fields: {
    name,
    typeId,
    assigneeId,
    copyToIds,
    instructions,
    clientNotes,
    notes,
    forClient,
    touched,
  },
  task,
  instructionsRef,
  notesRef,
  assigneeClientAndPatientIsValid,
  rootRef,
  selectedClientId,
  selectedPatientId,
  selectedAppointmentId,
  setSelectedAppointmentId,
  shouldUseVCRBehavior,
  setSlotInfo,
  slotInfo,
  soapId,
  taskRecurrenceSettingsRef,
}: TaskFormV2Props) => {
  const isContextItem = useIsCurrentContextItem(task)
  const { t } = useTranslation(['Tasks', 'Tooltips', 'Common'])

  const wellnessPlansEnabled = useSelector(
    getCurrentBusinessWellnessPlansEnabled,
  )
  const taskPermissions = useSelector(getCRUDByArea(PermissionArea.TASK))
  const isCvcRolesEnabled = useSelector(
    getFeatureToggle(FeatureToggle.CVC_ROLES),
  )
  const isFetching = useSelector(getTasksIsFetching)
  const isCurrentUserVCR = useHasRole(RoleName.VCR)

  const CreatableTaskTypes = useCreatableTaskTypes()
  const [openAddToAppointmentDialog, closeAddToAppointmentDialog] = useDialog(
    DialogNames.ADD_TO_APPOINTMENT,
  )

  const onAppointmentSelected = (newAppointmentId: string) => {
    closeAddToAppointmentDialog()
    setSelectedAppointmentId(newAppointmentId)
  }

  const onAddToAppointmentRequested = () => {
    openAddToAppointmentDialog({
      clientId: selectedClientId,
      patientId: selectedPatientId,
      title: t('Dialogs:ADD_TASK_TO_APPOINTMENT.TITLE'),
      onAdd: onAppointmentSelected,
      filterByClient: true,
    })
  }

  const isCreate = !task?.id

  const noPermission = !taskPermissions.update && isCvcRolesEnabled
  const isDisabled = !isContextItem || noPermission
  const showAssignClientAndPatientErrorMessage =
    touched && !assigneeClientAndPatientIsValid

  const { isLoading: isInitializingClientConcierge } =
    useInitializeClientConciergeOnCreation({
      copyToIds,
      isCreate,
    })

  return (
    <div className={styles.container}>
      <div className={styles.row}>
        <div className={styles.field}>
          <KibField
            required
            disabled={isDisabled}
            invalid={name.errors.length > 0}
            label={name.label!}
            maxLength={100}
            messagesContent={name.message}
            value={name.value}
            onChange={(e) => name.setValue(e.target.value)}
          />
        </div>
        <div className={styles.field}>
          <KibSelect
            disabled={isDisabled}
            label={typeId.label!}
            value={typeId.value}
            onChange={(e) => typeId.setValue(e.target.value)}
          >
            {CreatableTaskTypes.map((type) => (
              <option key={type.id} value={type.id}>
                {LanguageUtils.getTranslatedFieldName(type)}
              </option>
            ))}
          </KibSelect>
        </div>
      </div>

      <div>
        <TaskRecurrenceSettings
          isChirp
          appointmentId={selectedAppointmentId}
          disabled={noPermission}
          dueDate={dueDateProp}
          ref={taskRecurrenceSettingsRef}
          task={task}
        />
      </div>

      <ErrorTooltip
        message={
          !selectedClientId || !selectedPatientId
            ? t('Tooltips:CLIENT_AND_PATIENT_IS_REQUIRED')
            : t('Tooltips:CLIENT_NEEDS_TO_BE_A_BOOP_SYSTEM_USER')
        }
        open={showAssignClientAndPatientErrorMessage}
      >
        <div className={styles.row}>
          <ContactTargetSlot
            isChirp
            anchorRef={rootRef}
            disabled={isFetching || !isContextItem || noPermission}
            showAddContact={!forClient.value}
            onSlotChange={setSlotInfo}
            {...slotInfo}
          />
        </div>
      </ErrorTooltip>

      {selectedClientId && selectedPatientId && isContextItem && (
        <div className={styles.row}>
          <AddToAppointmentCard
            isChirp
            appointmentId={selectedAppointmentId}
            readOnly={noPermission}
            onAddToAppointmentRequested={onAddToAppointmentRequested}
            onEdit={onAddToAppointmentRequested}
          />
        </div>
      )}

      {!forClient.value && (
        <div className={styles.row}>
          <div className={styles.field}>
            <KibUserSelect
              disabled={shouldUseVCRBehavior && !isCurrentUserVCR}
              filterScope={UserSelectFilterScope.Staff}
              label={t('Common:ASSIGN_TEAM_MEMBER')}
              placeHolder={t('Common:SELECT_ACTION')}
              readOnly={isDisabled}
              teamFilter={TeamFilter.ALL}
              userSelectStateId="user-select-task-form-assignee"
              value={assigneeId.value}
              onChange={(selectedValue) => assigneeId.setValue(selectedValue)}
            />
          </div>
          <div className={styles.field}>
            <KibUserMultiSelect
              disabled={
                isFetching ||
                isInitializingClientConcierge ||
                !taskPermissions.update
              }
              filterScope={UserSelectFilterScope.Staff}
              isLoading={isInitializingClientConcierge || isFetching}
              label={t('Common:COPY_TO')}
              placeHolder={t('Common:SELECT_ACTION')}
              teamFilter={TeamFilter.ALL}
              userMultiSelectStateId="user-multi-select-task-form-copy-to"
              value={copyToIds.value}
              onChange={(values) => copyToIds.setValue(values)}
            />
          </div>
        </div>
      )}

      <div className={styles.row}>
        <NotesTemplateInput
          hidePanel
          isChirp
          disabled={noPermission}
          field={notes}
          isSoap={Boolean(soapId)}
          minHeight={2}
          ref={notesRef}
          soapId={soapId}
          title={
            forClient.value ? t('Common:INTERNAL_NOTES') : t('Common:NOTES')
          }
        />
      </div>

      {wellnessPlansEnabled && forClient.value && (
        <div className={styles.row}>
          <NotesTemplateInput
            hidePanel
            isChirp
            disabled={noPermission}
            field={instructions}
            isSoap={Boolean(soapId)}
            minHeight={2}
            ref={instructionsRef}
            soapId={soapId}
            title={instructions.label}
          />
        </div>
      )}

      {!isCreate && (
        <>
          {wellnessPlansEnabled && task?.completionDate && (
            <div>
              <div className={styles.taskCompletedContainer}>
                <div className={styles.titleContainer}>
                  <span className={styles.title}>
                    {t('Tasks:LABEL.TASK_COMPLETED')}
                  </span>
                </div>
                <div>
                  <span className={styles.text}>
                    {moment(task?.completionDate).format(
                      DateFormat.FULL_DATE_TIME_AT,
                    )}
                  </span>
                </div>
              </div>
              <KibDivider />
            </div>
          )}
          {wellnessPlansEnabled && clientNotes.value && (
            <div>
              <div className={styles.clientNotesContainer}>
                <div className={styles.titleContainer}>
                  <span className={styles.title}>{clientNotes.label}</span>
                </div>
                <div>
                  <span className={styles.text}>{clientNotes.value}</span>
                </div>
              </div>
              <KibDivider />
            </div>
          )}
        </>
      )}
    </div>
  )
}
